import { useProductsHook, useLanguageHook } from "../../../../../Hooks/use-context-hooks";
import { PageText } from "../../../../../components/Text/Text";
import {
    isEmptyList,
    FilterProductById,
    consolidateSpecListsToArray,
    extractProductNamesFromKeywords,
    findMatchingProducts
} from "../../../../../Utils/helper-functions";
import { ProductBackgroundShape } from "../../../../../components/Shape/ProductBackgroundShape";
import { AnimatedComponent } from "../../../../../Hooks/use-framer-motion";
import { GridSystem } from "../../../../Components/GridSystem/GridSystem";
import { ProductButton } from "../ProductButton/ProductButton";
import styles from "./MobileProductDetails.module.css";
import FeatureHeroResources from "../../../../Components/ProductElements/Details/FeatureResources/FeatureHeroResources";
import ColorLegend from "../../../../Components/ProductElements/Details/ColorLegend/ColorLegend";
import SpecificationColumns from "../../../../Components/ProductElements/Details/Specifications/SpecificationColumns";
import FeatureVideos from "../../../../Components/ProductElements/Details/FeatureVideo/FeatureVideos";
import FeatureResources from "../../../../Components/ProductElements/Details/FeatureResources/FeatureResources";
import UPCCodes from "../../../../Components/ProductElements/Details/UPCCodes/UPCCodes";
import FeatureLogos from "../../../../Components/ProductElements/Details/FeatureLogos/FeatureLogos";
import { MatchingProductComponent } from "../../../../Components/ProductElements/MatchingProduct/MatchingProductComponent";
import { DetailSection } from "../DetailSection/DetailSection";
import { isEolProduct } from "../../../../../Utils/product-availability";
import Product3DViewer from "../../../Home/Product3DViewer";
import Product3DViewerWithModal from "../../../Home/Product3DViewerWithModal";

export const MobileProductDetailsPage = ({ productId }) => {
    const { publicProducts } = useProductsHook();
    const { language } = useLanguageHook();

    const productDetails = FilterProductById(publicProducts, productId);
    const [productObject] = productDetails;

    if (!productObject) {
        return null;
    }

    const specsArray = consolidateSpecListsToArray(productObject);
    const relatedProducts = extractProductNamesFromKeywords(specsArray);
    const matchingProducts = findMatchingProducts(relatedProducts, publicProducts);

    const videoList = productObject?.videos;
    const hasVideos = !isEmptyList(videoList);

    const cleanedVideos = Array.isArray(videoList)
        ? videoList.filter(item => {
            if (typeof item === "string") return item.trim() !== "";
            return item != null;
        })
        : [];

    const isEol = isEolProduct(productObject);

    const availText = isEol
        ? "EOL"
        : productObject.availability || (language === "FR" ? "Disponible" : "");

    const interactiveImage =
        language === "EN"
            ? productObject.interactiveImageEN
            : productObject.interactiveImageFR;

    return (
        <div className={styles.pageContainer}>
            <GridSystem
                containerPaddingTop="2rem"
                containerPaddingBottom="2rem"
                containerBorderBottom="1px solid #D0CBC1"
            >
                <div className={styles.gridItem1}>
                    <div className={styles.mobileHeaderWrapper}>
                        <AnimatedComponent type="bounceEffect">
                            <PageText type="productPageTertiary">{availText}</PageText>
                        </AnimatedComponent>

                        <AnimatedComponent type="bounceEffect">
                            <PageText type="productPageTitle">{productObject.title}</PageText>
                        </AnimatedComponent>

                        <AnimatedComponent type="wipeEffect" directionStart="left" delay={0.1}>
                            <PageText type="productPageSubtitle">{productObject.subtitle}</PageText>
                        </AnimatedComponent>

                        <FeatureHeroResources product={productObject} />
                    </div>

                    <div className={styles.mobileProductImageWrapper}>
                        <ProductBackgroundShape className={styles.mobileProductImageShape} />

                        <div
                            className={`${styles.mobileProductImage} ${interactiveImage
                                ? styles.mobileProductImage3D
                                : styles.mobileProductImageStatic
                                }`}
                        >
                            {interactiveImage ? (
                                // <Product3DViewer
                                //     src={interactiveImage}
                                //     title={`${productObject.title} 3D Viewer`}
                                // />
                                <Product3DViewerWithModal
                                    src={interactiveImage}
                                    title={`${productObject.title} 3D Viewer`}
                                />
                            ) : (
                                <img
                                    loading="lazy"
                                    src={`${process.env.REACT_APP_AWS_URL}/${productObject.image}`}
                                    alt={productObject.title}
                                />
                            )}
                        </div>
                    </div>

                    <div className={styles.colorButtonWrapper}>
                        <div>
                            <ColorLegend colours={productObject.colour} />
                        </div>

                        {!isEol && (
                            <div className={styles.buttonWrapper}>
                                <ProductButton product={productObject} />
                            </div>
                        )}
                    </div>
                </div>
            </GridSystem>

            {/* <DetailSection containerBackgroundColor="var(--color-gray-06)">
                <SpecificationColumns product={productObject} />
            </DetailSection> */}
            <DetailSection
                key={`spec-section-${productObject._id}`}
                containerBackgroundColor="var(--color-gray-06)"
            >
                <SpecificationColumns
                    key={`spec-columns-${productObject._id}`}
                    product={productObject}
                />
            </DetailSection>

            {hasVideos && (
                <FeatureVideos product={{ ...productObject, videos: cleanedVideos }} />
            )}

            {productObject.sections && productObject.sections.length > 0 && (
                <DetailSection title="Resources" containerBackgroundColor="var(--color-gray-05)">
                    <FeatureResources product={productObject} />
                </DetailSection>
            )}

            {productObject.upcCode && productObject.upcCode.length > 0 && (
                <DetailSection title="UPC Codes" containerBackgroundColor="var(--color-gray-05)">
                    <UPCCodes product={productObject} />
                </DetailSection>
            )}

            {productObject.logos && productObject.logos.length > 0 && (
                <DetailSection title="Technology" containerBackgroundColor="var(--color-gray-05)">
                    <FeatureLogos product={productObject} />
                </DetailSection>
            )}

            {matchingProducts.length > 0 && (
                // <DetailSection title="Related home appliances" containerBackgroundColor="var(--color-gray-05)">
                <DetailSection title="Related products" containerBackgroundColor="var(--color-gray-05)">
                    <MatchingProductComponent product={matchingProducts} />
                </DetailSection>
            )}
        </div>
    );
};